import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { DatePipe } from '@angular/common';
import autoTable from 'jspdf-autotable';
import { DisplayService, ImageUtil, ValidationUtil } from "@qid/core";
import { Share } from '../model/Share';
import { ShareParticipantUtil } from "../utils/share-participant.util";
import { ParticipantRoles } from "../enum/participant-roles.enum";
import { DocumentAccessService } from "../../document-access";
import { ShareStatus } from "../enum/share-status.enum";


@Injectable({
  providedIn: 'root'
})
export class ExportPdfService {
  index: number = 0;
  totalShares: number = 0;
  isExporting: boolean = false;
  isCancelExport: boolean = false;

  constructor(
    private datePipe: DatePipe,
    private displayService: DisplayService,
    private documentAccessService: DocumentAccessService
  ) {
  }

  /**
   * Adds share content to PDF document without creating headers
   * Used for both individual and bulk exports
   */
  private async addShareContentToPdf(doc: jsPDF, share: Share): Promise<void> {
    let isFirstParticipantDocBlock = true;

    for (let participant of share.participants) {
      if (participant.roles.includes(ParticipantRoles.organiser)) continue;

      const participantDocumentsAccess = await this.getParticipantDocumentsAccess(participant);
      for (const access of participantDocumentsAccess) {
        await this.addParticipantDocumentPage(
          doc,
          share,
          participant,
          access,
          !isFirstParticipantDocBlock
        );
        isFirstParticipantDocBlock = false;
      }
    }
  }

  private async getParticipantDocumentsAccess(participant: any): Promise<any[]> {
    const documentsAccess: any[] = Array.isArray(participant?.documentsAccess)
      ? [...participant.documentsAccess]
      : [];

    if (participant?.sharedDocumentsIds && Array.isArray(participant.sharedDocumentsIds)) {
      for (const docId of participant.sharedDocumentsIds) {
        const res = await this.documentAccessService.findByDocumentId(docId);
        const status = Number(res?.httpStatusCode) || 0;
        if (status === 403 || status === 429 || (res?.errorMessage && !res?.data)) {
          throw new Error('EXPORT_RATE_LIMITED');
        }
        if (res?.data?.[0]) {
          documentsAccess.push(res.data[0]);
        }
      }
    }

    return documentsAccess;
  }

  private async addParticipantDocumentPage(
    doc: jsPDF,
    share: Share,
    participant: any,
    access: any,
    startOnNewPage: boolean
  ): Promise<void> {
    // Hard boundary: exactly one participant-doc block starts on a fresh page.
    if (startOnNewPage) {
      doc.addPage();
    }

    const dateToUse = share?.checkInData?.checkInTime || share?.createTime;
    const shareDetailRows = [
      ['Name', participant?.accountSnapshot?.name],
      ['Email', participant?.accountSnapshot?.email],
      ['Phone', participant?.accountSnapshot?.phoneNumber],
      ['Shared On', this.datePipe.transform(dateToUse, 'medium')],
    ];

    if (share?.template?.type === 'check-in') {
      shareDetailRows.push([
        'Check Out Date',
        share?.checkInData?.checkOutTime
          ? this.datePipe.transform(share.checkInData.checkOutTime, 'medium')
          : ''
      ]);
    }

    participant?.otherFields?.forEach(field => {
      shareDetailRows.push([
        ValidationUtil.isStringEmpty(field.label) ? field.name : field.label,
        field.value,
      ]);
    });

    autoTable(doc, {
      startY: startOnNewPage
        ? 12
        : ((doc as any).lastAutoTable?.finalY ? ((doc as any).lastAutoTable.finalY + 8) : 12),
      body: shareDetailRows,
      bodyStyles: {
        fontSize: 10,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
    });

    const idDetailRows = [
      [`${access?.document?.sid?.name} Number`, access?.documentData?.idNumber],
    ];
    access?.documentData?.manualFields?.forEach(field => {
      idDetailRows.push([
        ValidationUtil.isStringEmpty(field.label) ? field.name : field.label,
        field.value,
      ]);
    });

    autoTable(doc, {
      head: [[access?.document?.sid?.name, '']],
      headStyles: {
        fontSize: 12,
        fontStyle: 'bold',
        fillColor: [0, 0, 0],
        textColor: 'white',
      },
      body: idDetailRows,
      bodyStyles: {
        fontSize: 10,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
    });

    const exportImages = this.getExportImagesFromAccess(access);
    const startY = ((doc as any).lastAutoTable?.finalY ?? 80) + 6;
    if (exportImages.length < 1) {
      await this.addDocumentMockupPage(doc, access, false, startY);
      return;
    }

    await this.addImageGridToCurrentPage(doc, exportImages, startY);
  }

  private async addImageGridToCurrentPage(doc: jsPDF, images: Array<{ imageUrl: string }>, startY: number): Promise<void> {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 12;
    const marginBottom = 12;
    const gap = 5;
    const drawAreaWidth = pageWidth - marginX * 2;
    const drawAreaHeight = pageHeight - startY - marginBottom;
    const cols = images.length > 1 ? 2 : 1;
    const rows = Math.max(1, Math.ceil(images.length / cols));
    const cellWidth = (drawAreaWidth - (cols - 1) * gap) / cols;
    const cellHeight = (drawAreaHeight - (rows - 1) * gap) / rows;

    for (let index = 0; index < images.length; index++) {
      const src = ImageUtil.getImageSource(images[index].imageUrl);
      if (!src) continue;
      let dim;
      try {
        dim = await this.getImageDimensions(src);
      } catch {
        throw new Error('EXPORT_RATE_LIMITED');
      }
      const aspect = dim.width / dim.height;
      const cellCol = index % cols;
      const cellRow = Math.floor(index / cols);
      const cellX = marginX + cellCol * (cellWidth + gap);
      const cellY = startY + cellRow * (cellHeight + gap);

      let drawW = cellWidth;
      let drawH = drawW / aspect;
      if (drawH > cellHeight) {
        drawH = cellHeight;
        drawW = drawH * aspect;
      }
      const drawX = cellX + (cellWidth - drawW) / 2;
      const drawY = cellY + (cellHeight - drawH) / 2;
      doc.addImage(src, 'JPEG', drawX, drawY, drawW, drawH, undefined, 'FAST');
    }
  }

  public async exportIndividualPdf(share: Share, noSave: boolean = false): Promise<jsPDF | void> {
    // Check if share is completed
    if (share.status !== ShareStatus.completed) {
      await this.displayService.toast({ message: 'Only completed shares can be exported' });
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');

    const organiser = ShareParticipantUtil.findOrganisingParticipant(share.participants);

    // Add null safety check for organiser
    if (!organiser || !organiser.accountSnapshot) {
      console.error('No organiser found for share:', share._id);
      throw new Error('Cannot export PDF: No organiser found');
    }

    // Add header for individual export
    autoTable(doc, {
      head: [
        [
          {
            content: organiser.accountSnapshot.name,
            styles: {
              valign: 'bottom',
            },
          },
          {
            rowSpan: 2,
            content: 'qid',
            styles: {
              fontSize: 30,
              fontStyle: 'bolditalic',
              halign: 'right',
              valign: 'middle',
              cellPadding: { right: 10 },
            },
          },
        ],
        [
          {
            content: share && share.template ? share.template.purpose : '',
            styles: { fontSize: 10, fontStyle: 'normal', valign: 'top' },
          },
          '',
        ],
      ],
      headStyles: {
        fontSize: 15,
        fontStyle: 'bold',
        fillColor: [0, 0, 0],
        textColor: 'white',
        cellPadding: 4,
      },
    });

    // Use the extracted method to add content
    await this.addShareContentToPdf(doc, share);

    if (noSave) {
      return doc;
    }

    doc.output('dataurlnewwindow');
  }

  public async bulkExport(shares: Share[]): Promise<void> {
    // Filter only completed shares
    const completedShares = shares.filter(share => share.status === ShareStatus.completed);

    if (completedShares.length === 0) {
      await this.displayService.toast({ message: 'No completed shares to export' });
      return;
    }

    // Create single PDF document for all shares
    const doc = new jsPDF('p', 'mm', 'a4');
    let index = 1;
    this.index = 0;
    this.totalShares = completedShares.length;
    this.isExporting = true;

    for (const share of completedShares) {
      if (!this.isExporting) {
        break;
      }

      try {
        // Add divider/header for each share (only if not the first share)
        if (index > 1) {
          doc.addPage();
        }

        // Get organiser for this share
        const organiser = ShareParticipantUtil.findOrganisingParticipant(share.participants);

        // Add null safety check for organiser
        if (!organiser || !organiser.accountSnapshot) {
          console.error('No organiser found for share:', share._id);
          continue; // Skip this share and continue with next
        }

        // Add same header format as individual export
        autoTable(doc, {
          head: [
            [
              {
                content: organiser.accountSnapshot.name,
                styles: {
                  valign: 'bottom',
                },
              },
              {
                rowSpan: 2,
                content: 'qid',
                styles: {
                  fontSize: 30,
                  fontStyle: 'bolditalic',
                  halign: 'right',
                  valign: 'middle',
                  cellPadding: { right: 10 },
                },
              },
            ],
            [
              {
                content: share && share.template ? share.template.purpose : '',
                styles: { fontSize: 10, fontStyle: 'normal', valign: 'top' },
              },
              '',
            ],
          ],
          headStyles: {
            fontSize: 15,
            fontStyle: 'bold',
            fillColor: [0, 0, 0],
            textColor: 'white',
            cellPadding: 4,
          },
        });

        // Add export counter as a compact subtitle
        autoTable(doc, {
          body: [
            [
              {
                content: `Export ${index} of ${completedShares.length}`,
                styles: {
                  fontSize: 9,
                  fontStyle: 'italic',
                  halign: 'center',
                  textColor: [120, 120, 120],
                  cellPadding: { top: 2, bottom: 3 },
                },
              },
            ],
          ],
          bodyStyles: {
            fontSize: 9,
            lineWidth: 0,
          },
        });

        // Add share content using the extracted method
        await this.addShareContentToPdf(doc, share);

        await this.delay(100);
        this.index = index;
        index++;
      } catch (error) {
        this.stopExport();
        const isRateLimited = (error as Error)?.message === 'EXPORT_RATE_LIMITED';
        await this.displayService.toast({
          message: isRateLimited
            ? 'Unable to export PDF. Try exporting a smaller date range or Excel.'
            : 'PDF export failed. Please try again.',
          color: 'warning',
          duration: 4000,
        });
        return;
      }
    }

    if (!this.isCancelExport && this.isExporting) {
      doc.save('qid-collection-report.pdf');
      this.stopExport();
      await this.displayService.toast({ message: 'Export Completed' });
    }
  }

  public stopExport(): void {
    this.isExporting = false;
    this.index = 0;
    this.isCancelExport = false; // Reset cancel flag
  }

  public cancelExport(): void {
    this.stopExport();
    this.isCancelExport = true;
  }


  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private getExportImagesFromAccess(access: any): Array<{ imageUrl: string }> {
    // DigiLocker verified docs often stash only the face photo in scannedImages
    // (old ensureScannedImagePersistedForDigilocker). Prefer the card mockup.
    if (access?.documentData?.verifyTime) {
      return [];
    }

    const scannedImages = access?.documentData?.scannedImages;
    if (Array.isArray(scannedImages) && scannedImages.length > 0) {
      return scannedImages.filter((image) => !!image?.imageUrl);
    }

    return [];
  }

  private async addDocumentMockupPage(doc: jsPDF, access: any, startOnNewPage: boolean = true, startY?: number): Promise<void> {
    if (this.isAadhaarDocument(access)) {
      await this.addAadhaarMockupPage(doc, access, startOnNewPage, startY);
      return;
    }

    const sidName = access?.document?.sid?.name ?? 'Document';
    const docData = access?.documentData ?? {};

    if (startOnNewPage) {
      doc.addPage();
    }

    autoTable(doc, {
      head: [[`${sidName} (Verified Record)`, '']],
      headStyles: {
        fontSize: 12,
        fontStyle: 'bold',
        fillColor: [0, 0, 0],
        textColor: 'white',
      },
      body: [['']],
      bodyStyles: {
        minCellHeight: 85,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      didDrawCell: (data) => {
        if (data.column.index !== 0 || data.row.index !== 0 || data.cell.section !== 'body') {
          return;
        }

        const baseX = data.cell.x + 4;
        const baseY = data.cell.y + 6;
        const contentWidth = data.cell.width - 8;
        let cursorY = baseY;

        // Photo block (if available)
        const photo = this.getMockupPhotoUrl(docData);
        if (photo) {
          try {
            doc.addImage(photo, 'JPEG', baseX, cursorY, 28, 34, undefined, 'FAST');
          } catch (_) {
            // ignore image drawing failures and continue with text-only mockup
          }
        }

        const textStartX = photo ? baseX + 32 : baseX;
        const valueLines: string[] = [];
        if (docData?.fullName) valueLines.push(String(docData.fullName));
        if (docData?.dob) valueLines.push(`DOB: ${docData.dob}`);
        if (docData?.gender) valueLines.push(String(docData.gender));
        if (docData?.careOf) valueLines.push(String(docData.careOf));
        if (docData?.address?.addressLine) valueLines.push(String(docData.address.addressLine));
        const addressZip = docData?.address?.zipCode ? `- ${docData.address.zipCode}` : '';
        if (!docData?.address?.addressLine && addressZip) valueLines.push(addressZip);
        if (docData?.idNumber) valueLines.push(`ID: ${docData.idNumber}`);

        doc.setFontSize(10);
        const wrappedLines = doc.splitTextToSize(
          valueLines.length > 0 ? valueLines.join('\n') : 'No scanned image found. Exported from verified document fields.',
          contentWidth - (photo ? 32 : 0),
        );
        doc.text(wrappedLines, textStartX, cursorY + 4);
      },
    });
  }

  private async addAadhaarMockupPage(doc: jsPDF, access: any, startOnNewPage: boolean = true, startY?: number): Promise<void> {
    const docData = access?.documentData ?? {};
    const cardDataUrl = await this.renderAadhaarCardDataUrl(docData);
    if (!cardDataUrl) {
      this.addAadhaarTextFallback(doc, docData);
      return;
    }
    this.addRenderedCardToPdf(doc, cardDataUrl, 620, 430, startOnNewPage, startY);
  }

  private isAadhaarDocument(access: any): boolean {
    const sidKey = String(access?.document?.sid?.keyName ?? '').toUpperCase();
    const sidName = String(access?.document?.sid?.name ?? '').toLowerCase();
    return sidKey === 'ADHAR' || sidName === 'aadhaar' || sidName === 'aadhar';
  }

  private formatAadhaarNumber(idNumber: string): string {
    const clean = String(idNumber ?? '').replace(/\s+/g, '');
    if (clean.length === 12) {
      return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
    }
    return clean.length > 0 ? clean : 'XXXX XXXX XXXX';
  }

  private getAadhaarAddressLine(docData: any): string {
    const address = docData?.address ?? {};
    const parts = [
      address?.house,
      address?.street,
      address?.dist,
      address?.state,
    ].filter(Boolean);
    const zip = address?.zipCode ? ` - ${address.zipCode}` : '';
    return `${parts.join(', ')}${zip}`.trim();
  }

  private async renderAadhaarCardDataUrl(docData: any): Promise<string | null> {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 620;
      canvas.height = 430;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return null;
      }

      // Card background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#d0d0d0';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);

      const [headerImg, footerImg] = await Promise.all([
        this.loadFirstAvailableImage([
          '/assets/adhr-header.png',
          '/libs/common/assets/adhr-header.png',
        ]),
        this.loadFirstAvailableImage([
          '/assets/imgs/img.png',
          '/libs/core/src/assets/imgs/img.png',
        ]),
      ]);

      if (headerImg) {
        ctx.drawImage(headerImg, 26, 20, 568, 58);
      }
      if (footerImg) {
        ctx.drawImage(footerImg, 26, 368, 568, 34);
      }

      const photoUrl = this.getMockupPhotoUrl(docData);
      if (photoUrl) {
        const photo = await this.loadImage(photoUrl);
        if (photo) {
          ctx.drawImage(photo, 30, 96, 178, 178);
        }
      }

      ctx.fillStyle = '#111111';
      ctx.textBaseline = 'top';
      ctx.font = '500 20px Arial';
      const nameLines = this.toAadhaarNameLines(docData?.fullName, 24);
      let textY = 106;
      for (const line of nameLines) {
        ctx.fillText(line, 224, textY);
        textY += 32;
      }

      ctx.font = '500 19px Arial';
      if (docData?.dob) {
        ctx.fillText(`DOB: ${docData.dob}`, 224, textY + 2);
        textY += 30;
      }
      if (docData?.gender) {
        ctx.fillText(String(docData.gender), 224, textY + 2);
        textY += 26;
      }
      if (docData?.careOf) {
        ctx.font = '500 13px Arial';
        ctx.fillText(String(docData.careOf), 224, textY + 2, 352);
        textY += 20;
      }

      ctx.font = '500 12px Arial';
      const address = this.getAadhaarAddressLine(docData);
      if (address) {
        const addressLines = this.toAadhaarNameLines(address, 44);
        let addressY = Math.max(textY, 256);
        for (const line of addressLines.slice(0, 2)) {
          ctx.fillText(line, 224, addressY, 352);
          addressY += 16;
        }
      }

      ctx.font = '700 36px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(this.formatAadhaarNumber(docData?.idNumber), 310, 314);
      ctx.textAlign = 'left';

      return canvas.toDataURL('image/png');
    } catch (_) {
      return null;
    }
  }

  private async loadImage(src: string): Promise<HTMLImageElement | null> {
    if (!src) {
      return null;
    }
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }

  private async loadFirstAvailableImage(sources: string[]): Promise<HTMLImageElement | null> {
    for (const src of sources) {
      const img = await this.loadImage(src);
      if (img) {
        return img;
      }
    }
    return null;
  }

  private addRenderedCardToPdf(
    doc: jsPDF,
    cardDataUrl: string,
    sourceWidth: number,
    sourceHeight: number,
    startOnNewPage: boolean = true,
    startY?: number
  ): void {
    const marginX = 12;
    const pageWidth = doc.internal.pageSize.getWidth();
    const cardWidth = pageWidth - (marginX * 2);
    const cardHeight = (cardWidth * sourceHeight) / sourceWidth;
    const y = startY ?? 20;
    if (startOnNewPage) {
      doc.addPage();
    }

    doc.addImage(cardDataUrl, 'PNG', marginX, y, cardWidth, cardHeight, undefined, 'FAST');
  }

  private toAadhaarNameLines(value: string | undefined, maxCharsPerLine: number = 18): string[] {
    const cleaned = String(value ?? '').trim();
    if (!cleaned) {
      return ['-'];
    }
    const words = cleaned.split(/\s+/);
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
      if ((current + ' ' + word).trim().length > maxCharsPerLine && current.length > 0) {
        lines.push(current);
        current = word;
      } else {
        current = (current + ' ' + word).trim();
      }
    }
    if (current) {
      lines.push(current);
    }
    return lines.slice(0, 3);
  }

  private addAadhaarTextFallback(doc: jsPDF, docData: any): void {
    doc.setFontSize(14);
    doc.text('Aadhaar', 14, 25);
    doc.setFontSize(11);
    doc.text(`Name: ${docData?.fullName ?? '-'}`, 14, 36);
    if (docData?.dob) doc.text(`DOB: ${docData.dob}`, 14, 44);
    if (docData?.gender) doc.text(`Gender: ${docData.gender}`, 14, 52);
    const address = this.getAadhaarAddressLine(docData);
    if (address) doc.text(`Address: ${address}`, 14, 60);
    doc.text(`Aadhaar Number: ${this.formatAadhaarNumber(docData?.idNumber)}`, 14, 68);
  }

  private getMockupPhotoUrl(docData: any): string | null {
    const userPhotoURL = docData?.userPhotoURL;
    if (typeof userPhotoURL === 'string' && userPhotoURL.length > 0) {
      return ImageUtil.getImageSource(userPhotoURL);
    }

    const userPhotoURI = docData?.userPhotoURI;
    if (typeof userPhotoURI === 'string' && userPhotoURI.length > 0) {
      const normalizedUri = userPhotoURI.startsWith('data:')
        ? userPhotoURI
        : `data:image/png;base64,${userPhotoURI}`;
      return normalizedUri;
    }

    return null;
  }

  /**
   * Gets the dimensions of an image
   * @param imageSrc - The source of the image (URL or data URL)
   * @returns Promise<{width: number, height: number}> - Image dimensions
   */
  private async getImageDimensions(imageSrc: string): Promise<{width: number, height: number}> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for dimensions'));
      };

      img.src = imageSrc;
    });
  }

  /**
   * Crops an image to a specific aspect ratio using center crop
   * @param imageSrc - The source of the image (URL or data URL)
   * @param targetAspectRatio - The desired aspect ratio (width/height)
   * @returns Promise<string> - Data URL of the cropped image
   */
  private async cropImageToAspectRatio(imageSrc: string, targetAspectRatio: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS if needed

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          const originalWidth = img.width;
          const originalHeight = img.height;
          const originalAspectRatio = originalWidth / originalHeight;

          let cropWidth, cropHeight, cropX, cropY;

          if (originalAspectRatio > targetAspectRatio) {
            // Original image is wider - crop width
            cropHeight = originalHeight;
            cropWidth = originalHeight * targetAspectRatio;
            cropX = (originalWidth - cropWidth) / 2; // Center horizontally
            cropY = 0;
          } else {
            // Original image is taller - crop height
            cropWidth = originalWidth;
            cropHeight = originalWidth / targetAspectRatio;
            cropX = 0;
            cropY = (originalHeight - cropHeight) / 2; // Center vertically
          }

          // Set canvas size to cropped dimensions
          canvas.width = cropWidth;
          canvas.height = cropHeight;

          // Draw the cropped portion of the image
          ctx.drawImage(
            img,
            cropX, cropY, cropWidth, cropHeight, // Source rectangle
            0, 0, cropWidth, cropHeight // Destination rectangle
          );

          // Convert to data URL
          const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
          resolve(croppedDataUrl);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = imageSrc;
    });
  }

}

